<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  audienceList: {
    type: Object,
    default: null,
  },
  audienceLists: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('audienceLists/getUIFlags');

const dialogRef = ref(null);
const isLoadingOptions = ref(false);
const isLoadingPreview = ref(false);
const filterOptions = ref(null);
const preview = ref(null);

const DEFAULT_LIMIT = 250;
const POLL_INTERVAL_MS = 1500;

const state = reactive({
  targetAudienceIds: [],
  selectionLimit: DEFAULT_LIMIT,
  defaultCountryCode: '55',
  q: '',
  source_system: '',
  city: '',
  state: '',
  neighborhood: '',
  min_age: '',
  max_age: '',
  gender: '',
  marital_status: '',
  highest_education_level: '',
  desired_role: '',
  desired_area: '',
  skill: '',
  language: '',
  availability: '',
  min_experience_months: '',
  max_experience_months: '',
  min_salary_expectation: '',
  max_salary_expectation: '',
  accepts_home_office: false,
  accepts_internship: false,
  willing_to_travel: false,
  has_whatsapp: false,
  is_employed: false,
  allow_hire: false,
});

const audienceOptions = computed(() =>
  props.audienceLists.map(list => ({
    value: list.id,
    label: list.name,
  }))
);

const stateOptions = computed(() => filterOptions.value?.states || []);
const sourceOptions = computed(() => filterOptions.value?.source_systems || []);
const genderOptions = computed(() => filterOptions.value?.genders || []);
const maritalStatusOptions = computed(
  () => filterOptions.value?.marital_statuses || []
);
const educationOptions = computed(
  () => filterOptions.value?.education_levels || []
);
const desiredRoleOptions = computed(
  () => filterOptions.value?.desired_roles || []
);
const desiredAreaOptions = computed(
  () => filterOptions.value?.desired_areas || []
);
const skillOptions = computed(() => filterOptions.value?.skills || []);
const languageOptions = computed(() => filterOptions.value?.languages || []);
const availabilityOptions = computed(
  () => filterOptions.value?.availabilities || []
);

const summary = computed(() => preview.value?.summary || null);
const sample = computed(() => preview.value?.sample || []);
const isImporting = computed(() => uiFlags.value.isImporting);
const matchedCount = computed(() => Number(summary.value?.matched_count || 0));
const selectedCount = computed(() =>
  Number(summary.value?.selected_count || 0)
);
const canImport = computed(
  () =>
    state.targetAudienceIds.length > 0 &&
    selectedCount.value > 0 &&
    !isImporting.value
);

const formatNumber = value =>
  new Intl.NumberFormat().format(Number(value || 0));

const candidateInitials = candidate => {
  const names = candidate.name?.trim().split(/\s+/).filter(Boolean) || [];
  const initials = names
    .slice(0, 2)
    .map(name => name[0])
    .join('');

  return initials || String(candidate.id);
};

const candidateGenderLabel = gender => {
  const value = String(gender || '').toLowerCase();
  if (value === 'female') {
    return t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.VALUES.GENDER_FEMALE');
  }
  if (value === 'male') {
    return t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.VALUES.GENDER_MALE');
  }

  return gender;
};

const booleanRows = computed(() => [
  {
    key: 'accepts_home_office',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.HOME_OFFICE'),
  },
  {
    key: 'accepts_internship',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.INTERNSHIP'),
  },
  {
    key: 'willing_to_travel',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.TRAVEL'),
  },
  {
    key: 'has_whatsapp',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.WHATSAPP'),
  },
  {
    key: 'is_employed',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.EMPLOYED'),
  },
  {
    key: 'allow_hire',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BOOLEANS.AVAILABLE'),
  },
]);

const statCards = computed(() => [
  {
    key: 'matched',
    icon: 'i-lucide-users-round',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.STATS.MATCHED'),
    value: formatNumber(summary.value?.matched_count),
  },
  {
    key: 'reachable',
    icon: 'i-lucide-send',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.STATS.REACHABLE'),
    value: formatNumber(summary.value?.matched_reachable_count),
  },
  {
    key: 'selected',
    icon: 'i-lucide-user-round-plus',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.STATS.SELECTED'),
    value: formatNumber(summary.value?.selected_count),
  },
  {
    key: 'resume',
    icon: 'i-lucide-file-user',
    label: t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.STATS.RESUME'),
    value: formatNumber(summary.value?.with_resume_count),
  },
]);

const normalizePayload = () => {
  const payload = {
    q: state.q,
    source_system: state.source_system,
    city: state.city,
    state: state.state,
    neighborhood: state.neighborhood,
    min_age: state.min_age,
    max_age: state.max_age,
    gender: state.gender,
    marital_status: state.marital_status,
    highest_education_level: state.highest_education_level,
    desired_role: state.desired_role,
    desired_area: state.desired_area,
    skill: state.skill,
    language: state.language,
    availability: state.availability,
    min_experience_months: state.min_experience_months,
    max_experience_months: state.max_experience_months,
    min_salary_expectation: state.min_salary_expectation,
    max_salary_expectation: state.max_salary_expectation,
    accepts_home_office: state.accepts_home_office ? 'true' : '',
    accepts_internship: state.accepts_internship ? 'true' : '',
    willing_to_travel: state.willing_to_travel ? 'true' : '',
    has_whatsapp: state.has_whatsapp ? 'true' : '',
    is_employed: state.is_employed ? 'true' : '',
    allow_hire: state.allow_hire ? 'true' : '',
  };

  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== '' && value !== null
    )
  );
};

const resetState = () => {
  state.targetAudienceIds = props.audienceList ? [props.audienceList.id] : [];
  state.selectionLimit = DEFAULT_LIMIT;
  state.defaultCountryCode = '55';
  state.q = '';
  state.source_system = '';
  state.city = '';
  state.state = '';
  state.neighborhood = '';
  state.min_age = '';
  state.max_age = '';
  state.gender = '';
  state.marital_status = '';
  state.highest_education_level = '';
  state.desired_role = '';
  state.desired_area = '';
  state.skill = '';
  state.language = '';
  state.availability = '';
  state.min_experience_months = '';
  state.max_experience_months = '';
  state.min_salary_expectation = '';
  state.max_salary_expectation = '';
  state.accepts_home_office = false;
  state.accepts_internship = false;
  state.willing_to_travel = false;
  state.has_whatsapp = false;
  state.is_employed = false;
  state.allow_hire = false;
  preview.value = null;
};

const loadFilterOptions = async () => {
  if (filterOptions.value || isLoadingOptions.value) return;

  isLoadingOptions.value = true;
  try {
    filterOptions.value = await store.dispatch(
      'audienceLists/candidateFilterOptions'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.API.ERROR_MESSAGE')
    );
  } finally {
    isLoadingOptions.value = false;
  }
};

const loadPreview = async () => {
  isLoadingPreview.value = true;
  try {
    preview.value = await store.dispatch('audienceLists/candidatePreview', {
      filters: normalizePayload(),
      selectionLimit: state.selectionLimit,
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.API.ERROR_MESSAGE')
    );
  } finally {
    isLoadingPreview.value = false;
  }
};

const pollImport = async (audienceListId, importId) => {
  const record = await store.dispatch('audienceLists/pollImport', {
    id: audienceListId,
    importId,
  });
  if (!['completed', 'failed'].includes(record.status)) {
    setTimeout(() => pollImport(audienceListId, importId), POLL_INTERVAL_MS);
  }
};

const handleImport = async () => {
  if (!canImport.value) return;

  try {
    const filters = normalizePayload();
    const imports = await Promise.all(
      state.targetAudienceIds.map(id =>
        store.dispatch('audienceLists/createCandidateImport', {
          id,
          filters,
          selectionLimit: state.selectionLimit,
          defaultCountryCode: state.defaultCountryCode,
        })
      )
    );

    imports.forEach((record, index) => {
      pollImport(state.targetAudienceIds[index], record.id);
    });

    useAlert(
      t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.API.STARTED', {
        count: state.targetAudienceIds.length,
      })
    );
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.API.ERROR_MESSAGE')
    );
  }
};

const open = async () => {
  resetState();
  dialogRef.value?.open();
  await loadFilterOptions();
  await loadPreview();
};

defineExpose({ open, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    overflow-y-auto
    :title="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.TITLE')"
    :description="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BUTTONS.CLOSE')"
  >
    <div class="flex flex-col gap-5">
      <div class="grid gap-5 xl:grid-cols-[1.25fr,0.95fr]">
        <div
          class="flex flex-col gap-5 rounded-3xl border border-n-weak bg-gradient-to-br from-n-alpha-2 to-n-background p-5"
        >
          <div class="flex items-start gap-3">
            <span
              class="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-n-blue-3 text-n-blue-11"
            >
              <span class="i-lucide-sliders-horizontal size-5" />
            </span>
            <div class="flex flex-col gap-1">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FILTERS_TITLE') }}
              </h4>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FILTERS_SUBTITLE') }}
              </p>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <Input
              v-model="state.q"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.QUERY')"
              :placeholder="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.QUERY')
              "
            />
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.SOURCE') }}
              </label>
              <ComboBox
                v-model="state.source_system"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.SOURCE')
                "
                :options="sourceOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.SOURCE')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.STATE') }}
              </label>
              <ComboBox
                v-model="state.state"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.STATE')
                "
                :options="stateOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.STATE')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <Input
              v-model="state.city"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.CITY')"
              :placeholder="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.CITY')
              "
            />
            <Input
              v-model="state.neighborhood"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.NEIGHBORHOOD')
              "
              :placeholder="
                t(
                  'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.NEIGHBORHOOD'
                )
              "
            />
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.GENDER') }}
              </label>
              <ComboBox
                v-model="state.gender"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.GENDER')
                "
                :options="genderOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.GENDER')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MARITAL_STATUS')
                }}
              </label>
              <ComboBox
                v-model="state.marital_status"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MARITAL_STATUS')
                "
                :options="maritalStatusOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.MARITAL_STATUS'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.EDUCATION') }}
              </label>
              <ComboBox
                v-model="state.highest_education_level"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.EDUCATION')
                "
                :options="educationOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.EDUCATION')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.DESIRED_ROLE')
                }}
              </label>
              <ComboBox
                v-model="state.desired_role"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.DESIRED_ROLE')
                "
                :options="desiredRoleOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.DESIRED_ROLE'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.DESIRED_AREA')
                }}
              </label>
              <ComboBox
                v-model="state.desired_area"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.DESIRED_AREA')
                "
                :options="desiredAreaOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.DESIRED_AREA'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.SKILL') }}
              </label>
              <ComboBox
                v-model="state.skill"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.SKILL')
                "
                :options="skillOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.SKILL')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.LANGUAGE') }}
              </label>
              <ComboBox
                v-model="state.language"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.LANGUAGE')
                "
                :options="languageOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.LANGUAGE')
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.AVAILABILITY')
                }}
              </label>
              <ComboBox
                v-model="state.availability"
                :aria-label="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.AVAILABILITY')
                "
                :options="availabilityOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.AVAILABILITY'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <Input
              v-model="state.min_age"
              type="number"
              min="0"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MIN_AGE')"
            />
            <Input
              v-model="state.max_age"
              type="number"
              min="0"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MAX_AGE')"
            />
            <Input
              v-model="state.min_experience_months"
              type="number"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MIN_EXPERIENCE')
              "
            />
            <Input
              v-model="state.max_experience_months"
              type="number"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MAX_EXPERIENCE')
              "
            />
            <Input
              v-model="state.min_salary_expectation"
              type="number"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MIN_SALARY')"
            />
            <Input
              v-model="state.max_salary_expectation"
              type="number"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.MAX_SALARY')"
            />
          </div>

          <div
            class="grid gap-3 rounded-3xl border border-n-weak bg-n-background p-4 sm:grid-cols-2"
          >
            <div
              v-for="item in booleanRows"
              :key="item.key"
              class="flex items-center justify-between gap-3 rounded-2xl border border-transparent bg-n-alpha-1 px-3 py-2 transition-colors hover:border-n-weak hover:bg-n-alpha-2"
            >
              <span class="flex items-center gap-2 text-sm text-n-slate-12">
                <span class="i-lucide-badge-check size-4 text-n-brand" />
                {{ item.label }}
              </span>
              <Switch v-model="state[item.key]" />
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-4 rounded-3xl border border-n-weak bg-gradient-to-br from-n-alpha-2 to-n-background p-5 xl:sticky xl:top-0 xl:self-start"
        >
          <div class="flex items-start gap-3">
            <span
              class="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-send-horizontal size-5" />
            </span>
            <div class="flex flex-col gap-1">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.TARGETS_TITLE') }}
              </h4>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.TARGETS_SUBTITLE') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div>
              <p class="mb-1 text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.TARGETS') }}
              </p>
              <TagMultiSelectComboBox
                v-model="state.targetAudienceIds"
                :options="audienceOptions"
                :placeholder="
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PLACEHOLDERS.TARGETS')
                "
                class="[&>div]:bg-n-alpha-black2"
              />
            </div>

            <Input
              v-model="state.selectionLimit"
              type="number"
              min="1"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.SELECTION_LIMIT')
              "
              :message="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.HINTS.SELECTION_LIMIT')
              "
            />

            <Input
              v-model="state.defaultCountryCode"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.FIELDS.COUNTRY_CODE')
              "
              :message="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.HINTS.COUNTRY_CODE')
              "
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div
              v-for="stat in statCards"
              :key="stat.key"
              class="flex items-center gap-2 rounded-2xl border border-n-weak bg-n-background px-3 py-3"
            >
              <span
                class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-brand"
              >
                <span :class="stat.icon" class="size-4" />
              </span>
              <span class="flex min-w-0 flex-col">
                <span class="block text-lg font-semibold text-n-slate-12">
                  {{ stat.value }}
                </span>
                <span class="text-xs text-n-slate-11">{{ stat.label }}</span>
              </span>
            </div>
          </div>

          <div
            class="rounded-2xl border border-n-weak bg-n-background px-4 py-3 text-sm text-n-slate-11"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-shield-check size-5 shrink-0 text-n-brand"
              />
              <p class="mb-0">
                {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.SAFETY') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="faded"
              color="slate"
              icon="i-lucide-filter"
              :label="t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BUTTONS.PREVIEW')"
              class="w-full"
              :is-loading="isLoadingPreview"
              @click="loadPreview"
            />
            <Button
              icon="i-lucide-user-round-plus"
              :label="
                t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.BUTTONS.IMPORT', {
                  count: formatNumber(selectedCount),
                })
              "
              class="w-full"
              :disabled="!canImport"
              :is-loading="isImporting"
              @click="handleImport"
            />
          </div>
        </div>
      </div>

      <div class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
        <div
          class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="flex items-start gap-3">
            <span
              class="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-n-amber-3 text-n-amber-11"
            >
              <span class="i-lucide-id-card size-5" />
            </span>
            <div class="flex flex-col gap-1">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PREVIEW_TITLE', {
                    count: formatNumber(matchedCount),
                  })
                }}
              </h4>
              <p class="mb-0 text-sm text-n-slate-11">
                {{
                  t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.PREVIEW_DESCRIPTION')
                }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="isLoadingPreview || isLoadingOptions"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="!sample.length"
          class="rounded-2xl border border-dashed border-n-weak px-4 py-10 text-center text-sm text-n-slate-11"
        >
          {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.EMPTY') }}
        </div>

        <div v-else class="grid gap-4 lg:grid-cols-2">
          <div
            v-for="candidate in sample"
            :key="candidate.id"
            class="group overflow-hidden rounded-3xl border border-n-weak bg-n-background transition-all hover:-translate-y-0.5 hover:border-n-brand"
          >
            <div
              class="h-14 bg-gradient-to-r from-n-blue-3 via-n-teal-3 to-n-amber-3"
            />

            <div class="-mt-7 flex flex-col gap-4 px-4 pb-4">
              <div class="flex items-end justify-between gap-3">
                <div class="flex items-end gap-3">
                  <span
                    class="flex size-14 items-center justify-center rounded-2xl border-4 border-n-background bg-n-blue-5 text-sm font-semibold uppercase text-n-blue-11 shadow-sm"
                  >
                    {{ candidateInitials(candidate) }}
                  </span>
                  <div class="mb-1 flex min-w-0 flex-col gap-0.5">
                    <span
                      class="truncate text-base font-semibold text-n-slate-12"
                    >
                      {{ candidate.name || `#${candidate.id}` }}
                    </span>
                    <span
                      class="flex items-center gap-1 text-sm text-n-slate-11"
                    >
                      <span class="i-lucide-map-pin size-4" />
                      {{
                        [candidate.city, candidate.state]
                          .filter(Boolean)
                          .join(' · ') ||
                        t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.NO_LOCATION')
                      }}
                    </span>
                  </div>
                </div>
                <span
                  class="mb-1 rounded-full px-2.5 py-1 text-xs font-medium"
                  :class="
                    candidate.reachable
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{
                    candidate.reachable
                      ? t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.REACHABLE')
                      : t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.UNREACHABLE')
                  }}
                </span>
              </div>

              <div class="flex flex-wrap gap-2 text-xs text-n-slate-11">
                <span class="rounded-full bg-n-alpha-1 px-2.5 py-1">
                  {{
                    t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.EDUCATION', {
                      value: candidate.highest_education_level || '—',
                    })
                  }}
                </span>
                <span class="rounded-full bg-n-alpha-1 px-2.5 py-1">
                  {{
                    t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.EXPERIENCE', {
                      value: candidate.experience_months || 0,
                    })
                  }}
                </span>
                <span
                  v-if="candidate.age"
                  class="rounded-full bg-n-alpha-1 px-2.5 py-1"
                >
                  {{
                    t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.AGE', {
                      value: candidate.age,
                    })
                  }}
                </span>
                <span
                  v-if="candidate.gender"
                  class="rounded-full bg-n-alpha-1 px-2.5 py-1"
                >
                  {{
                    t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.GENDER', {
                      value: candidateGenderLabel(candidate.gender),
                    })
                  }}
                </span>
                <span
                  v-if="candidate.marital_status"
                  class="rounded-full bg-n-alpha-1 px-2.5 py-1"
                >
                  {{
                    t(
                      'CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.MARITAL_STATUS',
                      { value: candidate.marital_status }
                    )
                  }}
                </span>
                <span class="rounded-full bg-n-alpha-1 px-2.5 py-1">
                  {{
                    candidate.has_resume
                      ? t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.RESUME')
                      : t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.NO_RESUME')
                  }}
                </span>
              </div>

              <div class="flex flex-col gap-2 text-sm text-n-slate-11">
                <p v-if="candidate.desired_roles?.length" class="mb-0">
                  <span class="font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.ROLES') }}
                  </span>
                  {{ candidate.desired_roles.join(', ') }}
                </p>
                <p v-if="candidate.skills?.length" class="mb-0">
                  <span class="font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.SKILLS') }}
                  </span>
                  {{ candidate.skills.join(', ') }}
                </p>
                <p v-if="candidate.availabilities?.length" class="mb-0">
                  <span class="font-medium text-n-slate-12">
                    {{
                      t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.AVAILABILITY')
                    }}
                  </span>
                  {{ candidate.availabilities.join(', ') }}
                </p>
                <p
                  v-if="candidate.linked_audiences?.length"
                  class="mb-0 rounded-2xl bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-10"
                >
                  {{
                    t('CAMPAIGN.AUDIENCE.CANDIDATE_IMPORT.CARD.LINKED', {
                      names: candidate.linked_audiences.join(', '),
                    })
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
